import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CheckpointCard from '../components/CheckpointCard';

const API = '/api';

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }).toLowerCase();
}

// Defense in depth — backend already strips non-http(s) schemes, but never trust
// what we render. Returns a safe href, or null if the url should be dropped.
function safeHref(raw) {
  if (typeof raw !== 'string') return null;
  let url;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return url.href;
}

export default function UserProfile() {
  const { username } = useParams();
  const { user: currentUser, setUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editLinks, setEditLinks] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [check, setCheck] = useState({ status: 'idle', normalised: '', reason: '' });
  const [following, setFollowing] = useState(false);

  const isOwnProfile = currentUser?.username === username;

  // Debounced availability check while editing the username field
  useEffect(() => {
    if (!editing) return;
    const candidate = editUsername.trim();
    if (!candidate || candidate === profile?.username) {
      setCheck({ status: 'idle', normalised: '', reason: '' });
      return;
    }
    setCheck((c) => ({ ...c, status: 'checking' }));
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/users/check?u=${encodeURIComponent(candidate)}`);
        if (!r.ok) throw new Error('check failed');
        const d = await r.json();
        if (d.available) {
          setCheck({ status: 'available', normalised: d.normalised, reason: '' });
        } else if (d.reason === 'empty') {
          setCheck({ status: 'invalid', normalised: '', reason: 'empty' });
        } else {
          setCheck({ status: 'taken', normalised: d.normalised, reason: 'taken' });
        }
      } catch {
        setCheck({ status: 'idle', normalised: '', reason: '' });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [editUsername, editing, profile]);

  useEffect(() => { fetchProfile(); /* eslint-disable-next-line */ }, [username]);

  const authHeaders = () => {
    const h = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('logos_session_token');
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/users/${username}`, {
        credentials: 'include', headers: authHeaders()
      });
      if (response.ok) { setProfile(await response.json()); }
      else if (response.status === 404) { setError('user not found'); }
      else { throw new Error('failed to fetch profile'); }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const toggleFollow = async () => {
    if (!currentUser || following) return;
    setFollowing(true);
    const prev = profile.followed_by_me;
    const prevCount = profile.follower_count || 0;
    setProfile(p => ({ ...p, followed_by_me: !prev, follower_count: prevCount + (prev ? -1 : 1) }));
    try {
      const res = await fetch(`${API}/users/${username}/follow`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(p => ({ ...p, followed_by_me: data.following, follower_count: data.follower_count }));
      } else {
        setProfile(p => ({ ...p, followed_by_me: prev, follower_count: prevCount }));
      }
    } catch {
      setProfile(p => ({ ...p, followed_by_me: prev, follower_count: prevCount }));
    } finally { setFollowing(false); }
  };

  const startEdit = () => {
    setEditUsername(profile.username || '');
    setEditBio(profile.bio || '');
    setEditLinks((profile.links || []).join('\n'));
    setSaveError(null);
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('logos_session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const links = editLinks.split('\n').map(l => l.trim()).filter(Boolean);
      const cleanedUsername = editUsername.trim().toLowerCase();
      const usernameChanged = cleanedUsername && cleanedUsername !== profile.username;

      const res = await fetch(`${API}/users/me`, {
        method: 'PATCH', headers, credentials: 'include',
        body: JSON.stringify({
          username: cleanedUsername || profile.username,
          bio: editBio.trim() || null,
          links: links.length > 0 ? links : null,
        })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `failed to save (${res.status})`);
      }
      const updated = await res.json();
      setProfile(prev => ({ ...prev, ...updated }));
      // Refresh global auth state so nav re-renders with the new username
      setUser(prev => prev ? { ...prev, ...updated } : prev);
      setEditing(false);
      // Navigate to the new URL if username changed
      if (usernameChanged) navigate(`/u/${updated.username}`, { replace: true });
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <p className="text-[#888888] lowercase animate-pulse">loading profile...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center">
        <p className="text-2xl text-white lowercase mb-4">{error}</p>
        <Link to="/checkpoints" className="text-[#888888] lowercase hover:text-white transition-all duration-300">← browse models</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black py-12 px-6 lg:px-8 page-wrapper" data-testid="user-profile-page">
      <div className="max-w-5xl mx-auto">
        {/* Profile Header */}
        <div className="border-b border-[#1e251f] pb-10 mb-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#555555] mb-1.5">username</label>
                    <div className="relative">
                      <input
                        value={editUsername}
                        onChange={e => setEditUsername(e.target.value)}
                        placeholder="your-username"
                        maxLength={50}
                        className={`w-full bg-transparent border rounded-lg pl-4 pr-9 py-2 text-white lowercase text-lg outline-none transition-colors ${
                          check.status === 'available' ? 'border-[#3a9a4a] focus:border-[#4cb85c]' :
                          check.status === 'taken' || check.status === 'invalid' ? 'border-[#7a3030] focus:border-[#a04444]' :
                          'border-[#383f38] focus:border-white'
                        }`}
                        data-testid="edit-username"
                        autoFocus
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2" aria-hidden="true" data-testid="username-status">
                        {check.status === 'checking' && <span className="w-2 h-2 rounded-full bg-[#666666] block animate-pulse" />}
                        {check.status === 'available' && <span className="w-2 h-2 rounded-full bg-[#4cb85c] block" />}
                        {(check.status === 'taken' || check.status === 'invalid') && <span className="w-2 h-2 rounded-full bg-[#a04444] block" />}
                      </span>
                    </div>
                    {check.status === 'taken' && (
                      <p className="text-[#a04444] text-xs mt-1.5 lowercase">that username is already taken.</p>
                    )}
                    {check.status === 'invalid' && (
                      <p className="text-[#a04444] text-xs mt-1.5 lowercase">enter at least one character.</p>
                    )}
                    {check.status === 'available' && (
                      <p className="text-[#4cb85c] text-xs mt-1.5 lowercase">
                        available · profile will live at /u/{check.normalised}
                      </p>
                    )}
                    {check.status === 'idle' && (
                      <p className="text-[#555555] text-xs mt-1.5 lowercase">your profile lives at /u/{editUsername.trim().toLowerCase() || profile.username}</p>
                    )}
                  </div>
                  <textarea
                    value={editBio}
                    onChange={e => setEditBio(e.target.value)}
                    placeholder="write something about yourself..."
                    maxLength={500}
                    rows={3}
                    className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white lowercase text-sm outline-none focus:border-white transition-colors resize-none"
                    data-testid="edit-bio"
                  />
                  <textarea
                    value={editLinks}
                    onChange={e => setEditLinks(e.target.value)}
                    placeholder={"links (one per line)\nhttps://github.com/you\nhttps://your-site.com"}
                    rows={3}
                    className="w-full bg-transparent border border-[#383f38] rounded-lg px-4 py-2 text-white text-sm outline-none focus:border-white transition-colors resize-none font-mono"
                    data-testid="edit-links"
                  />
                  {saveError && (
                    <p className="text-[#ff8888] lowercase text-xs" data-testid="edit-error">{saveError}</p>
                  )}
                  <div className="flex gap-3">
                    <button onClick={saveProfile} disabled={saving || check.status === 'taken' || check.status === 'invalid' || check.status === 'checking'}
                      className="border border-[#383f38] px-5 py-2 text-white lowercase text-sm hover:bg-white hover:text-black transition-all duration-300 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-white font-mono" data-testid="save-profile-btn">
                      {saving ? 'saving...' : 'save'}
                    </button>
                    <button onClick={() => setEditing(false)} className="text-[#555555] lowercase text-sm hover:text-white transition-all duration-300">cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-2xl lg:text-3xl font-bold text-white lowercase" data-testid="profile-username">{profile.username}</h1>
                    {profile.verified && <img src="/phi-logo-64.png" alt="verified" title="verified" className="verified-badge" />}
                    {profile.role === 'admin' && <span className="text-[#555555] text-xs font-mono">admin</span>}
                  </div>
                  {profile.bio && <p className="text-[#888888] lowercase text-sm mt-2 leading-relaxed">{profile.bio}</p>}
                  {profile.links && profile.links.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {profile.links.map((link, i) => {
                        const href = safeHref(link);
                        if (!href) return null;
                        return (
                          <a
                            key={i}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="text-[#555555] font-mono text-xs hover:text-white transition-colors truncate max-w-[200px]"
                            data-testid={`profile-link-${i}`}
                          >
                            {href.replace(/^https?:\/\//, '')}
                          </a>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[#555555] lowercase text-xs mt-2">member since {formatDate(profile.created_at)}</p>
                </>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-6 text-sm mt-4">
                <div>
                  <span className="text-white">{profile.checkpoint_count}</span>
                  <span className="text-[#555555] ml-1.5">checkpoint{profile.checkpoint_count !== 1 ? 's' : ''}</span>
                </div>
                <div>
                  <span className="text-white">{(profile.total_stars || 0).toLocaleString()}</span>
                  <span className="text-[#555555] ml-1.5">star{profile.total_stars !== 1 ? 's' : ''}</span>
                </div>
                <div>
                  <span className="text-white">{profile.total_downloads.toLocaleString()}</span>
                  <span className="text-[#555555] ml-1.5">downloads</span>
                </div>
                <div>
                  <span className="text-white">{profile.follower_count || 0}</span>
                  <span className="text-[#555555] ml-1.5">follower{profile.follower_count !== 1 ? 's' : ''}</span>
                </div>
                <div>
                  <span className="text-white">{profile.following_count || 0}</span>
                  <span className="text-[#555555] ml-1.5">following</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 shrink-0">
              {isOwnProfile ? (
                <>
                  <Link to="/upload" className="border border-[#383f38] px-5 py-2.5 text-white lowercase hover:bg-white hover:text-black transition-all duration-300 rounded-lg glow-hover text-sm text-center" data-testid="upload-checkpoint-btn">
                    upload checkpoint
                  </Link>
                  {!editing && (
                    <button onClick={startEdit} className="text-[#555555] lowercase text-xs hover:text-white transition-all duration-300" data-testid="edit-profile-btn">
                      edit profile
                    </button>
                  )}
                </>
              ) : currentUser ? (
                <button
                  onClick={toggleFollow}
                  disabled={following}
                  className={`border px-5 py-2.5 rounded-lg text-sm lowercase text-center transition-all duration-300 ${
                    profile.followed_by_me
                      ? 'border-white text-white bg-white/10 hover:bg-white/20'
                      : 'border-[#383f38] text-white hover:bg-white hover:text-black glow-hover'
                  }`}
                  data-testid="follow-btn"
                >
                  {profile.followed_by_me ? 'following' : 'follow'}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Checkpoints */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-white lowercase font-mono">models</h2>
            {isOwnProfile && (
              <Link to="/workspace" className="text-[#555555] lowercase hover:text-white transition-all duration-300 text-sm font-mono" data-testid="profile-workspace-link">your workspace →</Link>
            )}
          </div>
          {isOwnProfile && (
            <p className="font-mono text-xs text-[#666666] lowercase mb-6">
              this is what everyone sees: your published models. private and unlisted ones are in your workspace.
            </p>
          )}

          {profile.checkpoints.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-checkpoints">
              <p className="text-[#555555] lowercase text-lg mb-2">no published models yet.</p>
              {isOwnProfile ? (
                <Link to="/workspace" className="inline-block mt-2 text-white lowercase hover:text-[#888888] transition-all duration-300 font-mono">
                  publish one from your workspace →
                </Link>
              ) : (
                <p className="text-[#333333] lowercase text-sm">this user hasn't published any models.</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]" data-testid="user-checkpoints-grid">
              {profile.checkpoints.map(checkpoint => (
                <CheckpointCard 
                  key={checkpoint.checkpoint_id} 
                  checkpoint={{ ...checkpoint, creator_username: profile.username, creator_verified: profile.verified }} 
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
